import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackAmpPageViewed } from '@/services/amplitudeService';

/**
 * Fires an Amplitude `Page Viewed` event on every route change. Renders
 * nothing. Must live inside <BrowserRouter> so `useLocation` has a router
 * context.
 *
 * We intentionally do NOT rely on Amplitude's autocapture page-view plugin —
 * the taxonomy is events-only and every tracked event is explicit so the
 * schema stays easy to reason about.
 *
 * Also re-fires Meta Pixel `PageView` and OpenAI Ads `page_viewed` on SPA
 * navigations. The base pixel snippets in index.html already fire on initial
 * load; this hook covers every subsequent client-side route change so both
 * pixels see the full funnel for ads optimization.
 */
export function AmplitudeRouteTracker() {
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    const fullPath = `${location.pathname}${location.search}`;
    if (lastPathRef.current === fullPath) return;
    lastPathRef.current = fullPath;
    trackAmpPageViewed(location.pathname, document.title || undefined);

    // Skip the very first render — index.html already fired PageView on load,
    // so firing again here would double-count the landing impression.
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
    if (typeof window.oaiq === 'function') {
      window.oaiq('measure', 'page_viewed', { type: 'contents' });
    }
  }, [location.pathname, location.search]);

  return null;
}

export default AmplitudeRouteTracker;
